import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";

function timeAgo(str) {
  if (!str) return "—";
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_COLORS = {
  pending:      { bg: "#FEF3C7", color: "#92400E" },
  reviewed:     { bg: "#DBEAFE", color: "#1D4ED8" },
  quoted:       { bg: "#D1FAE5", color: "#065F46" },
  closed:       { bg: "#F3F4F6", color: "#6B7280" },
  contacted:    { bg: "#DBEAFE", color: "#1D4ED8" },
  completed:    { bg: "#D1FAE5", color: "#065F46" },
  unread:       { bg: "#FEF3C7", color: "#92400E" },
  read:         { bg: "#F3F4F6", color: "#6B7280" },
  replied:      { bg: "#D1FAE5", color: "#065F46" },
  "in-progress":{ bg: "#DBEAFE", color: "#1D4ED8" },
};

const TYPE_CONFIG = {
  quote:          { label: "Quote Request",    color: "#002DB5", bg: "rgba(0,45,181,0.07)", route: "/admin/quotes" },
  consultation:   { label: "Consultation",     color: "#065F46", bg: "rgba(6,95,70,0.07)",  route: "/admin/consultations" },
  contact:        { label: "Message",          color: "#92400E", bg: "rgba(146,64,14,0.07)",route: "/admin/contacts" },
  "service-request": { label: "Service Request", color: "#6D28D9", bg: "rgba(109,40,217,0.07)", route: "/admin/service-requests" },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#F3F4F6", color: "#6B7280" };
  return (
    <span style={{ background: c.bg, color: c.color, padding: "2px 9px", borderRadius: "20px", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, fontFamily: "Arial, sans-serif" }}>
      {status}
    </span>
  );
}

function TypeBadge({ type }) {
  const c = TYPE_CONFIG[type] || { label: type, color: "#6B7280", bg: "#F3F4F6" };
  return (
    <span style={{ background: c.bg, color: c.color, padding: "2px 9px", borderRadius: "20px", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, fontFamily: "Arial, sans-serif" }}>
      {c.label}
    </span>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "240px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

function buildTimeline(data) {
  const items = [
    ...(data.quotes || []).map(q => ({
      _type: "quote", _id: q._id, status: q.status, createdAt: q.createdAt,
      title: `${q.firstName} ${q.lastName}`,
      sub: (q.selectedInsurance || []).join(", ") || "—",
      assignedTo: q.assignedTo,
    })),
    ...(data.consultations || []).map(c => ({
      _type: "consultation", _id: c._id, status: c.status, createdAt: c.createdAt,
      title: c.fullName,
      sub: `${c.contactMethod === "video" ? "Video" : "Phone"} · ${c.preferredDate || "No date specified"}`,
      assignedTo: c.assignedTo,
    })),
    ...(data.contacts || []).map(c => ({
      _type: "contact", _id: c._id, status: c.status, createdAt: c.createdAt,
      title: c.name,
      sub: c.subject || "—",
      assignedTo: c.assignedTo,
    })),
    ...(data.serviceRequests || []).map(r => ({
      _type: "service-request", _id: r._id, status: r.status, createdAt: r.createdAt,
      title: r.fullName,
      sub: r.requestType?.replace(/-/g, " ") || "—",
      assignedTo: r.assignedTo,
    })),
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  return items;
}

export default function AdminClientView() {
  usePageTitle("Client View");
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const emailParam     = searchParams.get("email") || "";

  const [searchInput, setSearchInput] = useState(emailParam);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [clientData,  setClientData]  = useState(null);

  const lookup = useCallback(async (email) => {
    if (!email?.trim()) return;
    setLoading(true);
    setError(null);
    setClientData(null);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/client?email=${encodeURIComponent(email.trim())}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Lookup failed.");
      if (data.success) setClientData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run lookup when email param changes from URL
  useEffect(() => {
    if (emailParam) { setSearchInput(emailParam); lookup(emailParam); }
  }, [emailParam, lookup]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    navigate(`/admin/client-view?email=${encodeURIComponent(searchInput.trim())}`);
  };

  const timeline = clientData ? buildTimeline(clientData) : [];
  const totalCount = timeline.length;

  return (
    <div style={{ maxWidth: "780px" }}>

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "22px", letterSpacing: "0.06em" }}>
          Client View
        </h1>
        <p className="font-constantia mt-1" style={{ fontSize: "14px", color: "#94a3b8" }}>
          Look up all submissions across every category for a single client email.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-3 mb-8">
        <input
          type="email"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="client@email.com"
          style={{
            flex: 1, fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72",
            background: "#fff", border: "1px solid rgba(1,46,114,0.15)", borderRadius: "12px",
            padding: "12px 16px", outline: "none",
          }}
          onFocus={e => { e.target.style.borderColor = "#002DB5"; e.target.style.boxShadow = "0 0 0 3px rgba(0,45,181,0.09)"; }}
          onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.15)"; e.target.style.boxShadow = "none"; }}
        />
        <button
          type="submit"
          disabled={loading || !searchInput.trim()}
          className="font-cinzel uppercase px-6 py-3 rounded-xl transition-all duration-200 disabled:opacity-50"
          style={{ fontSize: "10px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#012E72,#002DB5)", color: "#fff" }}
        >
          {loading ? "Searching…" : "Look Up"}
        </button>
      </form>

      {/* Loading */}
      {loading && <Spinner />}

      {/* Error */}
      {error && !loading && (
        <div className="px-4 py-3 rounded-xl mb-4" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <p className="font-constantia text-red-600" style={{ fontSize: "13px" }}>{error}</p>
        </div>
      )}

      {/* Results */}
      {clientData && !loading && (
        <>
          {/* Client header card */}
          <div className="rounded-2xl p-5 mb-5 flex items-center gap-4" style={{ background: "#fff", border: "1px solid rgba(1,46,114,0.08)" }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
              <span style={{ fontFamily: "Arial", fontSize: "18px", color: "#fff", fontWeight: 700 }}>
                {(clientData.name || clientData.email)[0].toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "16px", letterSpacing: "0.05em" }}>
                {clientData.name}
              </p>
              <a href={`mailto:${clientData.email}`} className="font-constantia" style={{ fontSize: "13px", color: "#94a3b8", textDecoration: "none" }}
                onMouseEnter={e => e.target.style.color = "#002DB5"} onMouseLeave={e => e.target.style.color = "#94a3b8"}>
                {clientData.email}
              </a>
            </div>
            {/* Summary counts */}
            <div className="hidden md:flex gap-5">
              {[
                { label: "Quotes",        count: clientData.quotes?.length },
                { label: "Consultations", count: clientData.consultations?.length },
                { label: "Messages",      count: clientData.contacts?.length },
                { label: "Service Req.",  count: clientData.serviceRequests?.length },
              ].map(({ label, count }) => (
                <div key={label} style={{ textAlign: "center" }}>
                  <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "18px" }}>{count}</p>
                  <p className="font-cinzel uppercase" style={{ fontSize: "8px", letterSpacing: "0.16em", color: "#94a3b8" }}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Empty state */}
          {totalCount === 0 ? (
            <div className="rounded-2xl p-12 text-center" style={{ background: "#fff", border: "1px solid rgba(1,46,114,0.08)" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <p className="font-cinzel uppercase" style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#cbd5e1" }}>
                No submissions found for this email
              </p>
            </div>
          ) : (
            <>
              <p className="font-cinzel uppercase mb-4" style={{ fontSize: "9px", letterSpacing: "0.22em", color: "#94a3b8" }}>
                {totalCount} submission{totalCount !== 1 ? "s" : ""} — most recent first
              </p>

              {/* Timeline */}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {timeline.map((item, i) => {
                  const tc = TYPE_CONFIG[item._type];
                  return (
                    <div
                      key={`${item._type}-${item._id}`}
                      className="rounded-2xl p-4 flex items-start gap-4 transition-all duration-150"
                      style={{ background: "#fff", border: "1px solid rgba(1,46,114,0.07)", cursor: "default" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(1,46,114,0.14)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(1,46,114,0.07)"}
                    >
                      {/* Type indicator */}
                      <div style={{ width: "36px", height: "36px", borderRadius: "10px", flexShrink: 0, background: tc?.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {item._type === "quote" && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tc.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        )}
                        {item._type === "consultation" && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tc.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        )}
                        {item._type === "contact" && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tc.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        )}
                        {item._type === "service-request" && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tc.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <TypeBadge type={item._type} />
                          <StatusBadge status={item.status} />
                          {item.assignedTo?.name && (
                            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.1em", color: "#94a3b8" }}>
                              → {item.assignedTo.name}
                            </span>
                          )}
                        </div>
                        <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>
                          {item.title}
                        </p>
                        <p className="font-constantia capitalize" style={{ fontSize: "12px", color: "#94a3b8" }}>
                          {item.sub}
                        </p>
                      </div>

                      {/* Time + link */}
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <p className="font-constantia" style={{ fontSize: "11px", color: "#94a3b8" }} title={formatDate(item.createdAt)}>
                          {timeAgo(item.createdAt)}
                        </p>
                        <button
                          onClick={() => navigate(tc.route)}
                          style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#002DB5", background: "none", border: "none", cursor: "pointer", marginTop: "4px", opacity: 0.7 }}
                          onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                          onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
                        >
                          View →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
